/**
 * Meldungs-Chat nach Freigabe (PRD §5.7, F37–F39): Melder ↔ Entwicklung.
 *
 * Sichtbar, sobald das Forgejo-Issue existiert. Nachrichten aus dem Portal
 * werden serverseitig ins Issue gespiegelt; Antworten der Entwicklung
 * kommen per Webhook zurück (nur @kunde-markierte Kommentare, F38).
 * Ungelesen-Logik (F39): zuletzt gesehene Anzahl im localStorage.
 */

import { useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Send } from "lucide-react"
import { toast } from "sonner"
import { api, apiError } from "@/services/api"

export const CHAT_GESEHEN_KEY = "meldungChatGesehen"

/** Merkt die gesehene Nachrichten-Anzahl (für das Badge in der Übersicht). */
export function chatGesehenMerken(reportId: number, anzahl: number) {
  try {
    localStorage.setItem(`${CHAT_GESEHEN_KEY}:${reportId}`, String(anzahl))
  } catch {
    /* Speicher voll/blockiert — Badge zeigt dann ggf. zu viel, kein Fehler */
  }
}

export function chatGesehene(reportId: number): number {
  const wert = localStorage.getItem(`${CHAT_GESEHEN_KEY}:${reportId}`)
  return wert ? Number(wert) : 0
}

const ROLLE_LABEL = { kunde: "Kunde", entwicklung: "Entwicklung" } as const

export function MeldungsChat({ reportId }: { reportId: number }) {
  const queryClient = useQueryClient()
  const [text, setText] = useState("")
  const endeRef = useRef<HTMLDivElement>(null)

  const { data: kommentare } = useQuery({
    queryKey: ["reports", reportId, "kommentare"],
    queryFn: () => api.reports.kommentare.list(reportId),
    refetchInterval: 30_000,
  })

  // F39: gesehen markieren + ans Ende scrollen, sobald Nachrichten da sind
  useEffect(() => {
    if (kommentare) {
      chatGesehenMerken(reportId, kommentare.length)
      endeRef.current?.scrollIntoView({ block: "nearest" })
    }
  }, [kommentare, reportId])

  const senden = useMutation({
    mutationFn: () => api.reports.kommentare.create(reportId, text.trim()),
    onSuccess: () => {
      setText("")
      void queryClient.invalidateQueries({ queryKey: ["reports", reportId, "kommentare"] })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const abschicken = () => {
    if (text.trim() && !senden.isPending) senden.mutate()
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex max-h-[380px] flex-col gap-2 overflow-y-auto pr-1">
        {(kommentare ?? []).map((k) => (
          <div
            key={k.id}
            className={`max-w-[85%] rounded-[10px] px-3.5 py-2.5 text-sm ${
              k.rolle === "kunde"
                ? "self-end bg-primary/10"
                : "self-start border bg-muted/40"
            }`}
          >
            <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {ROLLE_LABEL[k.rolle]}: {k.autorName || "unbekannt"} ·{" "}
              {new Date(k.erstelltAm).toLocaleString("de-DE")}
            </p>
            <p className="whitespace-pre-wrap leading-relaxed">{k.text}</p>
          </div>
        ))}
        {kommentare && kommentare.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Noch keine Nachrichten — Fragen zur Umsetzung kannst du hier direkt an die
            Entwicklung stellen.
          </p>
        )}
        <div ref={endeRef} />
      </div>

      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              abschicken()
            }
          }}
          rows={2}
          maxLength={4000}
          placeholder="Nachricht an die Entwicklung … (Enter sendet)"
          className="min-h-[44px] flex-1 rounded-lg border px-3 py-2 text-[13px]"
        />
        <button
          type="button"
          onClick={abschicken}
          disabled={!text.trim() || senden.isPending}
          className="flex h-[44px] items-center gap-1.5 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          Senden
        </button>
      </div>
    </div>
  )
}
